import React, { useState, useEffect } from 'react';
import './Support.css';

import { FaUser, FaEnvelope, FaTv, FaComment, FaAngleDown } from 'react-icons/fa';
import axios from "axios";

import { Heading, PageTitle } from '../../../components';
import { images, support } from '../../../constants';

// API path for sending form data
const API_PATH = 'https://www.example.com/api/phpmailer/support.php';

// Regular expressions for email validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;



const Support = () => {

  // State to manage form input values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    comment: ''
  });

  // State for handling success and error messages
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect to clear success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Function to handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Client-side validation
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.service) validationErrors.push('Service type is required');
      if (!formData.comment.trim()) validationErrors.push('Comment is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for API request
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Sending form data to API
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Request submission failed');
      }

      // On success, reset form and show success message
      setMailSent(true);
      setFormData({ name: '', email: '', service: '', comment: '' }); 
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle input changes and update state
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>

      {/* Page Title */}
      <PageTitle title={'Customer Support'} page={'Customer Support'} />

      <section className='support'>

        {/* Contact Information Section */}
        <div className='support-info'>

          {/* Loop through the contact array and display each contact info item */}
          {support.map((contact) => (
            <div className='info-item' key={contact.id}>

              {/* Display the contact icon */}
              <div className='icon'>{contact.icon}</div>

              {/* Display the contact title and details */}
              <div className='content'>
                <h3>{contact.title}</h3>
                {/* Loop through the content array and display each item as a paragraph */}
                {contact.content.map((value, index) => (
                  <p key={index}>{value}</p>
                ))}
              </div>

            </div>
          ))}

        </div>

        <div className='box-container'>

          {/* Support Image */}
          <div className='image'>
            <img src={images.support} alt='Customer Support' /> {/* Career Image */}
          </div>

          <form method="post" className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Contact Support'} subTitle={'Need Help?'} linear sideHeading />

            {/* Name Input Field */}
            <div className="input-box">
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input 
                  type="text" 
                  name="name" 
                  className="box" 
                  id="name" 
                  placeholder="Full Name" 
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  required 
                />
              </div>
            </div>
          
            {/* Email Input Field */}
            <div className="input-box">
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input 
                  type="email" 
                  name="email" 
                  className="box" 
                  id="email" 
                  placeholder="Your Email Address" 
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  required 
                />
              </div>
            </div>

            {/* Service Type Select Field */}
            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="service"><FaTv /></label>
                  <select 
                    className="select-box" 
                    id="service" 
                    name="service" 
                    value={formData.service}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    required
                  >
                    <option value="">Select Your Service</option>
                    <option value="internet">Internet</option>
                    <option value="cable-tv">Cable TV</option>
                    <option value="broadband">Broadband</option>
                    <option value="bundle">Internet + TV Bundle</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            {/* Comment/Message Textarea */}
            <div className="input-box">
              <div className="input-field text-area">
                <label htmlFor="comment"><FaComment /></label>
                <textarea 
                  cols="30" 
                  rows="10" 
                  name="comment" 
                  className="box" 
                  id="comment" 
                  placeholder="Describe Your Issue or Inquiry" 
                  value={formData.comment}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  required
                ></textarea>
              </div>
            </div>
          
            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              name="submit" 
              id="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          
            {/* Alert Message */}
            <span className="alert" id="msg">
              {error && (
                <div className="error">
                  {error.split(' | ').map((msg, i) => (
                    <span key={i}>{msg}</span>
                  ))}
                </div>
              )}
              {mailSent && <div className="success">Request submitted successfully</div>}
            </span>
          </form>

        </div>

      </section>

    </>
  );
};

export default Support;